import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Typography, Grid, Box } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';

import * as HrActions from '../../../store/actions/hr.action';
import { getUser } from '../../../store/selector/auth.selector';
import { getHrState } from '../../../store/selector/hr.selector';
import { columns } from './hr-list-column';
import Services from '../../../shared/services';
import SearchBar from '../../../shared/components/ui/dashboard-layout/search-bar';
import DynamicDataGrid from '../../../shared/components/ui/datagrid/datagrid';
import ExportFilterModal from '../../../shared/components/ui/modal/export-filter-modal';
import { IFilterForm } from '../../../shared/interfaces/filter-form.interface';

const styles = {
  buttonGrid: {
    mt: { md: 0, xs: '2rem' },
    display: 'flex',
    flexDirection: { sm: 'row', xs: 'column' },
    justifyContent: { md: 'flex-end', sm: 'flex-start', xs: 'center' },
  },

  addButton: {
    padding: '0.5rem 3rem',
    borderRadius: '2.5rem',
    width: { md: 'auto', sm: '100%' },
  },

  exportButton: {
    padding: '0.5rem 3rem',
    borderRadius: '2.5rem',
    width: { md: 'auto', sm: '100%' },
    ml: { sm: '0.5rem', xs: 0 },
    mt: { sm: 0, xs: '0.2rem' },
  },
};

const HrList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const { query } = useSelector(getHrState);
  /* eslint-disable camelcase */
  const {
    list: { pages, total, current_page, count, items },
  } = useSelector(getHrState);

  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const onModalAction = async (e: string, form: IFilterForm) => {
    if (e === 'confirm') {
      const { data, success, errors } = await Services.api.hr.applicationExport(null, form);
      if (success) {
        window.open(Services.api.hr.downloadHrApplicationList(data), '_blank');

        Services.toast('success', 'File successfully exported.');
      } else {
        const [{ message }] = errors;

        Services.toast('error', message);
      }

      setModalOpen(false);
    } else {
      setModalOpen(false);
    }
  };

  const onClickHandler = () => {
    setModalOpen(true);
  };

  useEffect(() => {
    if (query) dispatch(HrActions.HrFetchApplications(query));
  }, []);

  const addHandler = () => {
    navigate('/hr/add');
  };

  const SearchAction = (e: string) => {
    const q = { ...query, q: e };

    if (query) dispatch(HrActions.HrFetchApplications(q));
  };

  const onPaginationRequest = (page: number) => {
    const q = { ...query, page: page + 1 };

    if (q) dispatch(HrActions.HrFetchApplications(q));
  };

  return (
    <>
      <Typography sx={{ fontSize: '1.75rem', mb: '2rem' }}>List of Applications</Typography>

      <Grid
        container
        sx={{
          display: 'flex',
          justifyContent: { md: 'flex-end', sm: 'flex-start', xs: 'center' },
          mb: '2rem',
        }}
        columnSpacing={5}
      >
        <Grid item md={4} xs={12}>
          <SearchBar onSearchValueChange={(e: string) => SearchAction(e)} />
        </Grid>

        <Grid item md={8} xs={12} sx={styles.buttonGrid}>
          {!user.groups.includes('hr-bp') && (
            <Button
              startIcon={<PersonAddAltIcon />}
              sx={styles.addButton}
              variant='contained'
              disableElevation
              onClick={addHandler}
            >
              Add
            </Button>
          )}

          <Button
            startIcon={<FileDownloadIcon />}
            sx={styles.exportButton}
            variant={user.groups.includes('hr-bp') ? 'contained' : 'outlined'}
            onClick={onClickHandler}
            disableElevation
          >
            Export
          </Button>
        </Grid>
      </Grid>

      <Box sx={{ mt: '2rem', background: '#fff', height: '70vh', width: '100%' }}>
        {pages !== null && (
          <DynamicDataGrid
            onPagination={(page: number) => onPaginationRequest(page)}
            columns={columns}
            rows={items}
            config={{
              count: count,
              currentPage: current_page,
              pages: pages,
              total: total,
            }}
          />
        )}
      </Box>

      <ExportFilterModal
        isModalOpen={modalOpen}
        onClickModal={(e: string, form: IFilterForm) => onModalAction(e, form)}
      />
    </>
  );
};

export default HrList;
